import api from '../redux/api';

const CAMPAIGNS_BASE_URL = '/campaign';

const campaignsService = {
  /**
   * Get campaigns with pagination and filtering
   * @param {Object} params - Query parameters (status, page, limit, search, etc.)
   * @returns {Promise<Object>} Paginated campaigns data
   */
  getCampaigns: async (params = {}) => {
    try {
      console.log('🔍 DEBUG: API call with params:', params);
      const response = await api.get(CAMPAIGNS_BASE_URL, { params });
      console.log('🔍 DEBUG: Raw API response:', response);
      
      // Handle the backend response structure: response.data.data
      if (response.data && response.data.data) {
        console.log('🔍 DEBUG: Using response.data.data structure');
        const result = {
          data: Array.isArray(response.data.data) ? response.data.data : [],
          total: response.data.total || 0,
          page: response.data.page || 1,
          limit: response.data.limit || 10,
          totalPages: response.data.totalPages || 0
        };
        console.log('🔍 DEBUG: Processed result:', result);
        return result;
      }
      
      // Fallback for other response structures
      console.log('🔍 DEBUG: Using fallback response structure');
      const fallbackResult = {
        data: Array.isArray(response.data?.data) ? response.data.data :
              Array.isArray(response.data) ? response.data : [],
        total: response.data?.total || 0,
        page: response.data?.page || 1,
        limit: response.data?.limit || 10,
        totalPages: response.data?.totalPages || 0
      };
      console.log('🔍 DEBUG: Fallback result:', fallbackResult);
      return fallbackResult;
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      
      // Specifically handle 401 errors - let them propagate to global interceptor
      if (error.response?.status === 401) {
        console.log('401 error detected - propagating to global interceptor for forced logout');
        throw error;
      }
      
      // For other errors, maintain existing behavior
      return {
        data: [],
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0
      };
    }
  },

  /**
   * Get a specific campaign by ID
   * @param {string} campaignId - The ID of the campaign
   * @returns {Promise<Object>} Campaign data
   */
  getCampaignById: async (campaignId) => {
    try {
      const response = await api.get(`${CAMPAIGNS_BASE_URL}/${campaignId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching campaign ${campaignId}:`, error);
      throw error;
    }
  },

  /**
   * Create a new campaign
   * @param {Object} campaignData - The campaign data
   * @returns {Promise<Object>} Created campaign data
   */
  createCampaign: async (campaignData) => {
    try {
      const response = await api.post(CAMPAIGNS_BASE_URL, campaignData);
      return response.data;
    } catch (error) {
      console.error('Error creating campaign:', error);
      throw error;
    }
  },

  /**
   * Activate a campaign
   * @param {string} campaignId - The ID of the campaign to activate
   * @returns {Promise<Object>} Activation response
   */
  activateCampaign: async (campaignId, body) => {
    try {
      const payload =
        body != null && typeof body === 'object' && Object.keys(body).length > 0 ? body : undefined;
      const response = await api.patch(`${CAMPAIGNS_BASE_URL}/${campaignId}/activate`, payload);
      return response.data;
    } catch (error) {
      console.error(`Error activating campaign ${campaignId}:`, error);
      throw error;
    }
  },

  /**
   * Update a campaign
   * @param {string} campaignId - The ID of the campaign to update
   * @param {Object} updateData - The updated campaign data
   * @returns {Promise<Object>} Updated campaign data
   */
  updateCampaign: async (campaignId, updateData) => {
    try {
      const response = await api.patch(`${CAMPAIGNS_BASE_URL}/${campaignId}`, updateData);
      return response.data;
    } catch (error) {
      console.error(`Error updating campaign ${campaignId}:`, error);
      throw error;
    }
  },

  /**
   * Delete a campaign
   * @param {string} campaignId - The ID of the campaign to delete
   * @returns {Promise<Object>} Deletion response
   */
  deleteCampaign: async (campaignId) => {
    try {
      const response = await api.delete(`${CAMPAIGNS_BASE_URL}/${campaignId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting campaign ${campaignId}:`, error);
      throw error;
    }
  },

  /**
   * Get campaigns filtered by status for different tabs
   * @param {string} status - Campaign status ('active', 'upcoming', 'ended', 'drafts')
   * @param {Object} additionalParams - Additional query parameters
   * @returns {Promise<Object>} Filtered campaigns data
   */
  getCampaignsByStatus: async (status, additionalParams = {}) => {
    const params = {
      ...additionalParams,
    };

    const statusMapping = {
      'active': 'active',
      'upcoming': null,
      'ended': 'ended',
      'drafts': 'draft',
      'all': null
    };

    if (status === 'upcoming') {
      params.status = 'scheduled';
      params.startDate = new Date().toISOString();
    } else if (statusMapping[status] !== null && statusMapping[status] !== undefined) {
      params.status = statusMapping[status];
    }

    return this.getCampaigns(params);
  },

  /**
   * Search campaigns with query parameters
   * @param {string} query - Search query string
   * @param {Object} additionalParams - Additional query parameters
   * @returns {Promise<Object>} Search results
   */
  searchCampaigns: async (query, additionalParams = {}) => {
    const params = {
      ...additionalParams,
      search: query
    };

    return this.getCampaigns(params);
  }
};

export default campaignsService;